import { COUNTRIES } from './countries';
import { COUNTRY_DIAL_CODES } from './countryDialCodes';

/** International format: + followed by 8 to 15 digits, first digit non-zero. */
export const INTERNATIONAL_MOBILE_PHONE_PATTERN = /^\+[1-9]\d{7,14}$/;

/** Australian local format: 04 or 05 followed by 8 digits. */
export const AU_LOCAL_MOBILE_PHONE_PATTERN = /^0[45]\d{8}$/;

/** Either accepted format. Equivalent to the API's regular expression. */
export const MOBILE_PHONE_PATTERN = /^((\+[1-9]\d{7,14})|(0[45]\d{8}))$/;

export const MOBILE_PHONE_REQUIRED_MESSAGE = 'Mobile phone number is required';

export const MOBILE_PHONE_INVALID_MESSAGE =
  'Enter a valid mobile number, 8 to 15 digits including the country code';

/**
 * An Australian mobile in international form: +61, then 4 or 5, then eight digits.
 * This is the old 04XX XXX XXX rule kept in place, because the API's own rule accepts
 * any length from 8 to 15 digits — permissive enough to let a mistyped local number
 * through, which sends the verification link somewhere nobody answers. Christmas
 * Island and Cocos share +61 and the same numbering, so the rule holds for them too.
 */
const AU_INTERNATIONAL_MOBILE_PHONE_PATTERN = /^\+61[45]\d{8}$/;

export const AU_MOBILE_PHONE_INVALID_MESSAGE =
  'Enter an Australian mobile number, e.g. 0432 123 456';

/** Most digits an international number can hold, dialling code included. */
export const MOBILE_PHONE_MAX_DIGITS = 15;

/**
 * Removes whitespace, giving the value that is posted to the API. Nothing else is
 * removed: the API rejects any other punctuation, so brackets and hyphens must fail
 * validation rather than be silently cleaned up.
 */
export const normalizeMobilePhone = (value: string | undefined | null): string =>
  (value ?? '').replace(/\s+/g, '');

/** True, or the message saying why the number was rejected. */
export const validateMobilePhone = (value: string | undefined | null): true | string => {
  const normalized = normalizeMobilePhone(value);

  // Australian numbers are answered first, so that a half-typed or mistyped one gets
  // the Australian message rather than the general one. Telling someone who dropped a
  // digit from a local number to use international format sends them the wrong way.
  if (normalized.startsWith('+61') || normalized.startsWith('0')) {
    const valid =
      AU_INTERNATIONAL_MOBILE_PHONE_PATTERN.test(normalized) ||
      AU_LOCAL_MOBILE_PHONE_PATTERN.test(normalized);

    return valid || AU_MOBILE_PHONE_INVALID_MESSAGE;
  }

  if (!MOBILE_PHONE_PATTERN.test(normalized)) return MOBILE_PHONE_INVALID_MESSAGE;

  return true;
};

export const isValidMobilePhone = (value: string | undefined | null): boolean =>
  validateMobilePhone(value) === true;

/** react-hook-form rules for a mobile phone field. */
export const mobilePhoneRules = {
  required: MOBILE_PHONE_REQUIRED_MESSAGE,
  validate: validateMobilePhone,
};

/**
 * Which mobile phone error to show, if any. A half-typed number isn't a mistake yet,
 * so a format error waits until the field is left. A missing number still shows on
 * submit, since nothing else would tell the user why the form won't go.
 *
 * Submission alone isn't enough to reveal a format error: the multi-step flows submit
 * the whole form to move between steps, so isSubmitted is already true by the time the
 * individuals step is reached.
 */
export const visibleMobilePhoneError = <T extends { type?: string }>(
  error: T | undefined,
  isTouched: boolean,
  isSubmitted: boolean
): T | undefined => {
  if (!error) return undefined;

  // A missing number waits for the submit, so that moving through the country
  // dropdown doesn't announce a number the user hasn't reached yet.
  if (error.type === 'required') return isSubmitted ? error : undefined;

  return isTouched ? error : undefined;
};

// The country dropdown holds an ISO alpha-2 code rather than a name, because two
// countries can share a dialling code and the picked country has to survive a
// re-render. The address forms store names — these are separate fields.

export interface MobilePhoneCountryOption {
  /** ISO alpha-2 code, e.g. AU */
  value: string;
  /** e.g. "Australia (+61)" */
  label: string;
  dialCode: string;
  countryName: string;
}

export const DEFAULT_MOBILE_PHONE_COUNTRY = 'AU';

export const MOBILE_PHONE_COUNTRY_OPTIONS: MobilePhoneCountryOption[] = COUNTRIES.filter(
  (country) => COUNTRY_DIAL_CODES[country.value]
).map((country) => ({
  value: country.value,
  label: `${country.label} (${COUNTRY_DIAL_CODES[country.value]})`,
  dialCode: COUNTRY_DIAL_CODES[country.value],
  countryName: country.label,
}));

/**
 * Which country to show when a dialling code is shared by several. A stored number
 * carries no country, so +1 has to resolve to one of its twenty-odd countries: the
 * largest is the least surprising guess, and the user can correct it.
 */
const PRIMARY_COUNTRY_BY_DIAL_CODE: Record<string, string> = {
  '+1': 'US',
  '+7': 'RU',
  '+39': 'IT',
  '+44': 'GB',
  '+47': 'NO',
  '+61': 'AU',
  '+64': 'NZ',
  '+212': 'MA',
  '+262': 'RE',
  '+358': 'FI',
  '+500': 'FK',
  '+590': 'GP',
  '+599': 'CW',
  '+672': 'NF',
};

export const dialCodeForCountry = (countryCode: string): string =>
  COUNTRY_DIAL_CODES[countryCode] ?? COUNTRY_DIAL_CODES[DEFAULT_MOBILE_PHONE_COUNTRY];

/** Digits left for the number itself once the dialling code is accounted for. */
export const nationalNumberMaxLength = (dialCode: string): number =>
  MOBILE_PHONE_MAX_DIGITS - dialCode.replace(/\D/g, '').length;

/**
 * Builds the value posted to the API. The leading zero of a local number is dropped,
 * since 0432 123 456 dialled from outside Australia is +61 432 123 456.
 */
export const composeMobilePhone = (dialCode: string, nationalNumber: string): string => {
  const digits = (nationalNumber ?? '').replace(/\D/g, '').replace(/^0+/, '');
  return digits ? `${dialCode}${digits}` : '';
};

/** Longest dialling code the value begins with, if any. */
const matchDialCode = (normalized: string): string | undefined =>
  MOBILE_PHONE_COUNTRY_OPTIONS.filter((option) => normalized.startsWith(option.dialCode)).reduce<
    string | undefined
  >(
    (longest, option) =>
      !longest || option.dialCode.length > longest.length ? option.dialCode : longest,
    undefined
  );

/**
 * Groups digits in threes, joining a trailing single digit to the group before it so
 * a ten-digit number reads 415 555 2671 rather than 415 555 267 1.
 */
const groupDigits = (digits: string): string => {
  const groups = digits.match(/\d{1,3}/g) ?? [];

  if (groups.length > 1 && groups[groups.length - 1].length === 1) {
    groups[groups.length - 2] += groups.pop();
  }

  return groups.join(' ');
};

/**
 * Formats a stored number for reading, e.g. +61 432 123 456. Numbers held in the
 * older local format keep their familiar 0432 123 456 grouping, and anything that
 * doesn't parse is shown as stored rather than being mangled into shape.
 */
export const formatMobilePhoneForDisplay = (value: string | undefined | null): string => {
  const normalized = normalizeMobilePhone(value);
  if (!normalized) return '';

  if (!normalized.startsWith('+')) {
    const digits = normalized.replace(/\D/g, '');
    const local = digits.match(/^(\d{4})(\d{3})(\d{3})$/);
    return local ? `${local[1]} ${local[2]} ${local[3]}` : digits;
  }

  const dialCode = matchDialCode(normalized);
  if (!dialCode) return normalized;

  return `${dialCode} ${groupDigits(normalized.slice(dialCode.length))}`.trim();
};

/**
 * Splits a stored number back into a country and the digits that follow its dialling
 * code, so an existing record can be edited. A value with no leading + is treated as
 * a local number for the default country, which is how records were stored before the
 * country dropdown existed. Where several countries share a dialling code the longest
 * match wins, and the default country is preferred over its neighbours.
 */
export const splitMobilePhone = (
  value: string | undefined | null
): { country: string; nationalNumber: string } => {
  const normalized = normalizeMobilePhone(value);

  if (!normalized.startsWith('+')) {
    return { country: DEFAULT_MOBILE_PHONE_COUNTRY, nationalNumber: normalized.replace(/\D/g, '') };
  }

  const matches = MOBILE_PHONE_COUNTRY_OPTIONS.filter((option) =>
    normalized.startsWith(option.dialCode)
  );

  if (!matches.length) {
    return { country: DEFAULT_MOBILE_PHONE_COUNTRY, nationalNumber: normalized.replace(/\D/g, '') };
  }

  const longest = matches.reduce((best, option) =>
    option.dialCode.length > best.dialCode.length ? option : best
  );
  const primary = PRIMARY_COUNTRY_BY_DIAL_CODE[longest.dialCode];
  const match =
    matches.find((option) => option.dialCode === longest.dialCode && option.value === primary) ??
    longest;

  return {
    country: match.value,
    nationalNumber: normalized.slice(match.dialCode.length).replace(/\D/g, ''),
  };
};
